import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
// The cross-tool workflow: one proactive request → a cascade of autonomous
// actions across real tools → a tangible deliverable. This is the AHA: "I asked
// for one outcome and it quietly did five real things and handed me the result."
const WORKFLOW_PROMPT = 'prep me for my 2pm with Acme and move the deal forward.';
type ActionStep = {
  slug: string;
  name: string;
  label: string;
  light?: boolean;
};
const ACTION_STEPS: ActionStep[] = [{
  slug: 'hubspot',
  name: 'HubSpot',
  label: 'Pulled the Acme deal — $48k, stage Proposal Sent'
}, {
  slug: 'gmail',
  name: 'Gmail',
  label: 'Read the email thread and flagged 2 open questions'
}, {
  slug: 'googlecalendar',
  name: 'Calendar',
  label: 'Reviewed the 2pm invite and the attendee list'
}, {
  slug: 'notion',
  name: 'Notion',
  label: 'Built a one-page prep brief with talking points',
  light: true
}, {
  slug: 'gmail',
  name: 'Gmail',
  label: 'Drafted the follow-up email — ready to send'
}];
function ActionStepRow({
  step,
  index,
  isActive,
  isDone,
  reducedMotion
}: {
  step: ActionStep;
  index: number;
  isActive: boolean;
  isDone: boolean;
  reducedMotion: boolean;
}) {
  return <motion.div initial={reducedMotion ? {
    opacity: 1,
    x: 0
  } : {
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.4,
    delay: reducedMotion ? 0 : index * 0.15
  }} className="flex items-center gap-4 p-4 rounded-xl bg-white border border-neutral-200/70 shadow-sm" data-id="element-67">
      {/* Tool icon */}
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#FAF8F4] shrink-0" data-id="element-68">
        <img src={`https://cdn.simpleicons.org/${step.slug}${step.light ? '/2B211A' : ''}`} alt={step.name} className="w-5 h-5 object-contain" loading="lazy" onError={e => {
        const img = e.currentTarget as HTMLImageElement;
        if (!img.dataset.fallback) {
          img.dataset.fallback = '1';
          img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${step.slug}.svg`;
        } else {
          img.style.visibility = 'hidden';
        }
      }} data-id="element-69" />
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0" data-id="element-70">
        <p className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-71">
          {step.label}
        </p>
      </div>

      {/* Status indicator */}
      <div className="shrink-0" data-id="element-72">
        {isDone ? <motion.div initial={reducedMotion ? {
        scale: 1
      } : {
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 18
      }} className="flex items-center justify-center w-6 h-6 rounded-full bg-[#EA580C] text-white" data-id="element-73">
            <Check size={14} strokeWidth={3} data-id="element-74" />
          </motion.div> : isActive ? <motion.div animate={reducedMotion ? {} : {
        rotate: 360
      }} transition={reducedMotion ? {} : {
        duration: 1,
        repeat: Infinity,
        ease: 'linear'
      }} className="text-[#EA580C]" data-id="element-75">
            <Loader2 size={20} strokeWidth={2.5} data-id="element-76" />
          </motion.div> : <div className="w-6 h-6 rounded-full border-2 border-neutral-200" data-id="element-77" />}
      </div>
    </motion.div>;
}
function DeliverableCard({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  return <motion.div initial={reducedMotion ? {
    opacity: 1,
    y: 0,
    scale: 1
  } : {
    opacity: 0,
    y: 24,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    type: 'spring',
    stiffness: 220,
    damping: 24,
    delay: reducedMotion ? 0 : 0.3
  }} className="relative text-left rounded-2xl bg-white border border-neutral-200/70 shadow-[0_12px_40px_rgba(43,33,26,0.08)] overflow-hidden" data-id="element-78">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-neutral-100" data-id="element-79">
        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#EA580C] shrink-0" data-id="element-80">
          <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-81" />
        </span>
        <div className="min-w-0" data-id="element-82">
          <p className="text-sm font-semibold text-[#2B211A] leading-tight" data-id="element-83">
            Giga
          </p>
          <p className="text-xs text-neutral-400 leading-tight" data-id="element-84">
            Morning brief · just now
          </p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EA580C]/10 border border-[#EA580C]/25 text-xs font-bold uppercase tracking-wide text-[#C2410C] shrink-0" data-id="element-85">
          <Check size={12} strokeWidth={3} data-id="element-86" />
          Done
        </span>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-3" data-id="element-87">
        <p className="text-base font-semibold text-[#2B211A] leading-relaxed" data-id="element-88">
          Your Acme prep brief is ready — and the deal's moving.
        </p>
        <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-89">
          I pulled the deal, read the thread, and built your one-page brief with
          talking points for the 2pm. Your follow-up email is drafted and ready
          to send the moment you give the nod.
        </p>
      </div>
    </motion.div>;
}
export function ActionTrace() {
  const reducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showDeliverable, setShowDeliverable] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, {
    once: true,
    margin: '-100px'
  });
  // Sequence: wait a beat after in-view, then tick through each action step,
  // then reveal the deliverable card.
  useEffect(() => {
    if (!inView || reducedMotion) {
      // In reduced motion, show everything immediately
      if (reducedMotion && inView) {
        setActiveIndex(ACTION_STEPS.length - 1);
        setShowDeliverable(true);
      }
      return;
    }
    // Start the sequence after a brief delay
    const startDelay = setTimeout(() => {
      setActiveIndex(0);
    }, 600);
    return () => clearTimeout(startDelay);
  }, [inView, reducedMotion]);
  useEffect(() => {
    if (reducedMotion || !inView || activeIndex < 0) return;
    if (activeIndex < ACTION_STEPS.length - 1) {
      // Move to next action after a brief "working" period
      const nextTimer = setTimeout(() => {
        setActiveIndex(i => i + 1);
      }, 800);
      return () => clearTimeout(nextTimer);
    } else {
      // All actions done — reveal the deliverable
      const doneTimer = setTimeout(() => {
        setShowDeliverable(true);
      }, 900);
      return () => clearTimeout(doneTimer);
    }
  }, [activeIndex, inView, reducedMotion]);
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32" data-id="element-90">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-91">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 md:mb-20" data-id="element-92">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-93">
            Action over answers
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-6" data-id="element-94">
            Built to do the work — not just talk about it.
          </h2>
          <p className="text-base md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto" data-id="element-95">
            One request. A cascade of autonomous actions across your tools. A
            finished result — ready when you are.
          </p>
        </div>

        {/* Prompt */}
        <div ref={containerRef} className="max-w-3xl mx-auto mb-12" data-id="element-96">
          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-97">
            <p className="text-2xl md:text-4xl font-title font-medium text-[#2B211A] leading-snug" data-id="element-98">
              <span className="text-[#EA580C] font-semibold" data-id="element-99">@Giga</span>{' '}
              {WORKFLOW_PROMPT}
            </p>
          </motion.div>
        </div>

        {/* Action steps */}
        <div className="max-w-3xl mx-auto space-y-3 mb-12" data-id="element-100">
          {ACTION_STEPS.map((step, index) => <ActionStepRow key={index} step={step} index={index} isActive={activeIndex === index} isDone={activeIndex > index} reducedMotion={!!reducedMotion} data-id="element-101" />)}
        </div>

        {/* Deliverable */}
        {(showDeliverable || reducedMotion) && <div className="max-w-2xl mx-auto" data-id="element-102">
            <motion.p initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-xs font-semibold tracking-[4px] uppercase text-neutral-400 mb-6 text-center" data-id="element-103">
              And here's what shows up done
            </motion.p>
            <DeliverableCard reducedMotion={!!reducedMotion} data-id="element-104" />
          </div>}
      </div>
    </section>;
}